import React, { useState, useEffect, useCallback } from 'react';
import MaterialTable from 'material-table';
import { useSelector } from "react-redux";
import {
  Box,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  Snackbar
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import {
  modernTableStyle,
  modernTableOptions,
  modernTableIcons,
  modernTableLocalization,
  PageHeader,
  TableEditInput,
  TableSelectEditInput
} from '../common/tableTheme';
import { supabase } from '../config/SupabaseConfig';

const generateSlug = (text) => {
  return (text || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export default function TenantsList() {
  const auth = useSelector(state => state.auth);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Notificações
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast(prev => ({ ...prev, open: false }));
  };

  const isSuperAdmin = Boolean(
    auth.profile?.is_superadmin ||
    auth.profile?.usertype === 'superadmin'
  );

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      if (!supabase) {
        showToast('Cliente Supabase não inicializado.', 'error');
        return;
      }

      const { data: tenants, error } = await supabase
        .from('tenants')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao listar tenants:', error);
        showToast(`Erro ao carregar tenants: ${error.message}`, 'error');
        return;
      }

      setData(tenants || []);
    } catch (e) {
      console.error('Exceção ao buscar tenants:', e);
      showToast('Falha na comunicação com o banco de dados.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const copyToClipboard = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    showToast(`ID copiado: ${text}`, 'success');
    setTimeout(() => setCopiedId(null), 2500);
  };

  const columns = [
    {
      title: 'Empresa / Nome',
      field: 'name',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            bgcolor: '#eff6ff',
            color: '#2563eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #dbeafe',
            flexShrink: 0
          }}>
            <BusinessIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#0f172a', lineHeight: 1.3 }}>
              {rowData.name}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>
              /{rowData.slug}
            </Typography>
          </Box>
        </Box>
      ),
      editComponent: props => (
        <TableEditInput
          placeholder="Ex: Minas Car Logística"
          value={props.value || ''}
          onChange={props.onChange}
          autoFocus
        />
      )
    },
    {
      title: 'Slug (URL / Identificador)',
      field: 'slug',
      render: rowData => (
        <Chip
          label={rowData.slug}
          size="small"
          sx={{
            fontFamily: 'monospace',
            fontWeight: 600,
            fontSize: '11px',
            bgcolor: '#f1f5f9',
            color: '#334155'
          }}
        />
      ),
      editComponent: props => (
        <TableEditInput
          placeholder="Deixe vazio para gerar do nome"
          value={props.value || ''}
          onChange={props.onChange}
        />
      )
    },
    {
      title: 'CNPJ',
      field: 'cnpj',
      render: rowData => (
        <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '12px', color: '#334155' }}>
          {rowData.cnpj || '-'}
        </Typography>
      ),
      editComponent: props => (
        <TableEditInput
          placeholder="00.000.000/0001-00"
          value={props.value || ''}
          onChange={props.onChange}
        />
      )
    },
    {
      title: 'Tenant UUID',
      field: 'id',
      editable: 'never',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{
            fontFamily: 'monospace',
            fontSize: '11px',
            color: '#64748b',
            maxWidth: 160,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {rowData.id || '-'}
          </Typography>
          {rowData.id && (
            <Tooltip title={copiedId === rowData.id ? "Copiado!" : "Copiar ID do Tenant"}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(rowData.id, rowData.id);
                }}
                sx={{
                  p: 0.5,
                  color: copiedId === rowData.id ? '#16a34a' : '#94a3b8',
                  '&:hover': { color: '#2563eb' }
                }}
              >
                {copiedId === rowData.id ? (
                  <CheckIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    },
    {
      title: 'Status',
      field: 'status',
      initialEditValue: 'active',
      lookup: { active: 'Ativo', trial: 'Trial', suspended: 'Suspenso' },
      render: rowData => {
        const status = rowData.status || 'active';
        let bg = '#dcfce7';
        let color = '#15803d';
        let border = '#bbf7d0';
        let label = 'Ativo';

        if (status === 'trial') {
          bg = '#eff6ff';
          color = '#1d4ed8';
          border = '#bfdbfe';
          label = 'Trial';
        } else if (status === 'suspended') {
          bg = '#fee2e2';
          color = '#b91c1c';
          border = '#fecaca';
          label = 'Suspenso';
        }

        return (
          <Chip
            label={label}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: '11px',
              bgcolor: bg,
              color: color,
              border: `1px solid ${border}`
            }}
          />
        );
      },
      editComponent: props => (
        <TableSelectEditInput
          value={props.value || 'active'}
          onChange={props.onChange}
          options={[
            { value: 'active', label: 'Ativo' },
            { value: 'trial', label: 'Trial' },
            { value: 'suspended', label: 'Suspenso' }
          ]}
        />
      )
    },
    {
      title: 'Data Cadastro',
      field: 'created_at',
      editable: 'never',
      render: rowData => (
        <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
          {rowData.created_at ? new Date(rowData.created_at).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          }) : '-'}
        </Typography>
      )
    }
  ];

  return (
    <Box sx={{ width: '100%', p: 0.5 }}>
      <PageHeader
        title="Gestão de Tenants (Empresas)"
        subtitle="Isolamento multi-tenancy e controle global de frotas clientes da plataforma Alpha Giro."
        count={data.length}
      />

      {!isSuperAdmin && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
          <strong>Atenção:</strong> Apenas perfis com permissão de <strong>SuperAdmin</strong> possuem autorização para criar, editar ou excluir empresas clientes no banco de dados.
        </Alert>
      )}

      <MaterialTable
        title=""
        columns={columns}
        style={modernTableStyle}
        icons={modernTableIcons}
        localization={modernTableLocalization}
        data={data}
        isLoading={loading}
        onRowClick={((evt, selected) => setSelectedRow(selected.tableData?.id))}
        options={modernTableOptions(selectedRow, {
          searchPlaceholder: "Buscar por nome, slug ou CNPJ...",
          pageSize: 10
        })}
        editable={{
          onRowAdd: newData =>
            new Promise(async (resolve, reject) => {
              if (!newData.name || !newData.name.trim()) {
                showToast('Informe o nome da empresa.', 'error');
                reject();
                return;
              }

              if (!supabase) {
                showToast('Supabase não conectado.', 'error');
                reject();
                return;
              }

              const cleanName = newData.name.trim();
              const finalSlug = (newData.slug && newData.slug.trim())
                ? generateSlug(newData.slug)
                : generateSlug(cleanName);

              const payload = {
                name: cleanName,
                slug: finalSlug,
                cnpj: (newData.cnpj || '').trim(),
                status: newData.status || 'active'
              };

              try {
                const { data: inserted, error } = await supabase
                  .from('tenants')
                  .insert([payload])
                  .select()
                  .single();

                if (error) {
                  console.error('Erro ao inserir tenant:', error);
                  showToast(`Erro ao criar tenant: ${error.message}`, 'error');
                  reject();
                  return;
                }

                if (inserted) {
                  setData(prev => [inserted, ...prev]);
                  showToast(`Tenant "${inserted.name}" cadastrado com sucesso!`, 'success');
                  resolve();
                } else {
                  fetchTenants();
                  resolve();
                }
              } catch (err) {
                console.error('Exceção ao criar tenant:', err);
                showToast('Erro interno ao salvar no banco.', 'error');
                reject();
              }
            }),

          onRowUpdate: (newData, oldData) =>
            new Promise(async (resolve, reject) => {
              if (!newData.name || !newData.name.trim()) {
                showToast('O nome da empresa é obrigatório.', 'error');
                reject();
                return;
              }

              if (!oldData.id) {
                showToast('ID do tenant não encontrado.', 'error');
                reject();
                return;
              }

              if (!supabase) {
                showToast('Supabase não conectado.', 'error');
                reject();
                return;
              }

              const cleanName = newData.name.trim();
              const finalSlug = (newData.slug && newData.slug.trim())
                ? generateSlug(newData.slug)
                : generateSlug(cleanName);

              const payload = {
                name: cleanName,
                slug: finalSlug,
                cnpj: (newData.cnpj || '').trim(),
                status: newData.status || 'active',
                updated_at: new Date().toISOString()
              };

              try {
                const { data: updated, error } = await supabase
                  .from('tenants')
                  .update(payload)
                  .eq('id', oldData.id)
                  .select()
                  .single();

                if (error) {
                  console.error('Erro ao atualizar tenant:', error);
                  showToast(`Erro ao atualizar: ${error.message}`, 'error');
                  reject();
                  return;
                }

                const finalRecord = updated || { ...oldData, ...payload };
                setData(prev => prev.map(t => t.id === oldData.id ? finalRecord : t));
                showToast(`Tenant "${cleanName}" atualizado com sucesso!`, 'success');
                resolve();
              } catch (err) {
                console.error('Exceção ao atualizar tenant:', err);
                showToast('Erro ao atualizar no banco.', 'error');
                reject();
              }
            }),

          onRowDelete: oldData =>
            new Promise(async (resolve, reject) => {
              if (!oldData.id) {
                showToast('ID do tenant não encontrado.', 'error');
                reject();
                return;
              }

              if (!supabase) {
                showToast('Supabase não conectado.', 'error');
                reject();
                return;
              }

              try {
                const { error } = await supabase
                  .from('tenants')
                  .delete()
                  .eq('id', oldData.id);

                if (error) {
                  console.error('Erro ao excluir tenant:', error);
                  if (error.code === '23503') {
                    showToast('Não é possível excluir: existem frotas, motoristas ou rotas vinculados a esta empresa.', 'error');
                  } else {
                    showToast(`Erro ao excluir: ${error.message}`, 'error');
                  }
                  reject();
                  return;
                }

                setData(prev => prev.filter(t => t.id !== oldData.id));
                showToast(`Tenant "${oldData.name}" removido com sucesso.`, 'success');
                resolve();
              } catch (err) {
                console.error('Exceção ao excluir tenant:', err);
                showToast('Erro ao excluir no banco.', 'error');
                reject();
              }
            })
        }}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', borderRadius: '8px' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
